/**
 * Implementation details + Exceptions documentation can be found here:
 * https://www.vocalink.com/tools/modulus-checking/
 *
 * Exceptions were added for version v7-90
 * valid from 8 JULY 2024
 */
import {
  ApplyAccountNumberSubstitutionsOptions,
  ApplySortCodeSubstitutionsOptions,
  ModulusCheckOverrides,
  OverrideTestOptions,
  ReplaceWeightOptions,
  Weight,
  positions,
  zeroise,
  zeroise_u_b,
} from './modulus-model';
import { getCheckFromWeightTable } from './modulus-validator';

/**
 * Base Exceptionhandling
 *
 * should be returned if a check has no exception
 */
export const baseExceptionHandling: ModulusCheckOverrides = {
  replaceWeight: (opts: ReplaceWeightOptions) => opts.weight,
  applyAccountNumberSubstitutions: (
    opts: ApplyAccountNumberSubstitutionsOptions,
  ) => opts.accountNumber,
  applySortCodeSubstitutions: (opts: ApplySortCodeSubstitutionsOptions) =>
    opts.sortCode,
  overrideTest: undefined,
};

/**
 * ## Exception 1
 *
 * Perform the double alternate check except:
 * - Add 27 to the total (ie before you divide by 10)
 *
 * This effectively places a financial institution number (580149) before the sorting code and account
 * number which is subject to the alternate doubling as well.
 */
export const exception1: ModulusCheckOverrides = {
  ...baseExceptionHandling,
  overrideTest: alternateTest1,
};

function alternateTest1(opts: OverrideTestOptions): boolean {
  const { sum } = opts;
  return (opts.sum + 27) % 10 === 0;
}

/**
 * ## Exception 2
 *
 * Only occurs for some standard modulus 11 checks, when there is a 2 in the exception column for the
 * first check for a sorting code and a 9 in the exception column for the second check for the same
 * sorting code. This is used specifically for Lloyds euro accounts.
 */
export const exception2: ModulusCheckOverrides = {
  ...baseExceptionHandling,
  applySortCodeSubstitutions: substituteForException2,
  replaceWeight: replaceForException2,
};

function replaceForException2(opts: ReplaceWeightOptions): Weight {
  const { account, weight, checkNumber, weightTable } = opts;
  if (checkNumber === 1 && weightTable) {
    return getCheckFromWeightTable(account.slice(0, 6).join(''), weightTable)[0]
      .weight;
  }

  if (account[positions['a']] !== 0 && account[positions['g']] !== 9) {
    return {
      u: 0,
      v: 0,
      w: 1,
      x: 2,
      y: 5,
      z: 3,
      a: 6,
      b: 4,
      c: 8,
      d: 7,
      e: 10,
      f: 9,
      g: 3,
      h: 1,
    };
  } else if (account[positions['a']] !== 0 && account[positions['g']] === 9) {
    return {
      u: 0,
      v: 0,
      w: 0,
      x: 0,
      y: 0,
      z: 0,
      a: 0,
      b: 0,
      c: 8,
      d: 7,
      e: 10,
      f: 9,
      g: 3,
      h: 1,
    };
  }

  return weight;
}

function substituteForException2(
  opts: ApplySortCodeSubstitutionsOptions,
): string {
  if (opts.checkNumber === 1) {
    return '309634';
  }
  return opts.sortCode;
}

/**
 * ## Exception 3
 *
 * If c=6 or c=9 the double alternate check does not need to be carried out
 */
export const exception3: ModulusCheckOverrides = {
  ...baseExceptionHandling,
  replaceWeight: replaceForException3,
};

function replaceForException3(opts: ReplaceWeightOptions): Weight {
  const { account, weight } = opts;

  if (account[positions['c']] === 6 || account[positions['c']] === 9) {
    return zeroise;
  }

  return weight;
}

/**
 * ## Exception 4
 *
 * Perform the standard modulus 11 check.
 *
 * After you have finished the check, ensure that the remainder is the same as the two-digit checkdigit;
 * the checkdigit for exception 4 is gh from the original account number
 */
export const exception4: ModulusCheckOverrides = {
  ...baseExceptionHandling,
  overrideTest: alternateTest4,
};

function alternateTest4(opts: OverrideTestOptions): boolean {
  const { sum, account } = opts;

  const remainder = sum % 11;

  const g = account[positions['g']];
  const h = account[positions['h']];

  return remainder === Number(`${g}${h}`);
}

/**
 * ## Exception 5
 *
 * Perform the first check (standard modulus check) except:
 * -  If the sorting code appears in this table in the “Original s/c” column, substitute it for the “substitute with”
 * column (for check purposes only). If the sorting code is not found, use the original sorting code.
 *
 * For the standard check with exception 5 the checkdigit is g from the original account number.
 * After dividing the result by 11:
 * - if the remainder = 0 and g = 0 the account number is valid
 * - if the remainder = 1 the account number is invalid
 * - for all other remainders, take the remainder away from 11. If the number you get is the same as g then
 * the account number is valid.
 *
 * Perform the second double alternate check, and for the double alternate check with exception 5 the
 * checkdigit is hfrom the original account number, except:
 * After dividing the result by 10:
 * - if the remainder = 0 and h = 0 the account number is valid
 * - for all other remainders, take the remainder away from 10. If the number you get is the same as h then
 * the account number is valid.
 */
export const exception5: ModulusCheckOverrides = {
  ...baseExceptionHandling,
  applySortCodeSubstitutions: substituteForException5,
  overrideTest: alternateTest5,
};

function substituteForException5(
  opts: ApplySortCodeSubstitutionsOptions,
): string {
  const { sortCode } = opts;
  let newSortCode = sortCode;

  exception5Substitutions.forEach((sub) => {
    if (sub[0] === sortCode) {
      newSortCode = sub[1];
    }
  });

  return newSortCode;
}

function alternateTest5(opts: OverrideTestOptions): boolean {
  const { sum, account, algorithm } = opts;

  if (algorithm === 'MOD11') {
    const g = account[positions['g']];

    const remainder = sum % 11;

    if (remainder === 0 && g === 0) {
      return true;
    } else if (remainder === 1) {
      return false;
    } else if (11 - remainder === g) {
      return true;
    } else {
      return false;
    }
  }

  if (algorithm === 'DBLAL') {
    const h = account[positions['h']];
    const remainder = sum % 10;

    if (remainder === 0 && h === 0) {
      return true;
    } else if (10 - remainder === h) {
      return true;
    } else {
      return false;
    }
  }
  return true;
}

// TODO: fetch file from web-page & parse
const exception5Substitutions = [
  ['938173', '938017'],
  ['938289', '938068'],
  ['938297', '938076'],
  ['938600', '938611'],
  ['938602', '938343'],
  ['938604', '938603'],
  ['938608', '938408'],
  ['938609', '938424'],
  ['938613', '938017'],
  ['938616', '938068'],
  ['938618', '938657'],
  ['938620', '938343'],
  ['938622', '938130'],
  ['938628', '938181'],
  ['938643', '938246'],
  ['938647', '938611'],
  ['938648', '938246'],
  ['938649', '938394'],
  ['938651', '938335'],
  ['938653', '938424'],
  ['938654', '938621'],
];

/**
 * ## Exception 6
 *
 * Indicates that these sorting codes may contain foreign currency accounts which cannot be checked.
 * Perform the first check, and if present perform the second check, and note that in either check:
 *
 * -  If a = 4, 5, 6, 7 or 8, and g and h are the same, the accounts are for a foreign currency and the checks
 * cannot be used.
 */
export const exception6: ModulusCheckOverrides = {
  ...baseExceptionHandling,
  replaceWeight: replaceForException6,
};

function replaceForException6(opts: ReplaceWeightOptions): Weight {
  const { account, weight } = opts;

  const a = account[positions['a']];
  const g = account[positions['g']];
  const h = account[positions['h']];

  if ([4, 5, 6, 7, 8].includes(a) && g === h) {
    return zeroise;
  }

  return weight;
}

/**
 * ## Exception 7
 *
 * Perform the check as specified, except if g = 9 zeroise weighting positions u-b.
 */
export const exception7: ModulusCheckOverrides = {
  ...baseExceptionHandling,
  replaceWeight: replaceForException7,
};

function replaceForException7(opts: ReplaceWeightOptions): Weight {
  const { account, weight } = opts;

  const g = account[positions['g']];

  if (g === 9) {
    return zeroise_u_b(weight);
  }

  return weight;
}

/**
 * ## Exception 8
 *
 * Perform the check as specified, except substitute the sorting code with 090126, for check purposes
 * only.
 */
export const exception8: ModulusCheckOverrides = {
  ...baseExceptionHandling,
  applySortCodeSubstitutions: substituteForException8,
};

function substituteForException8(): string {
  return '090126';
}

/**
 * ## Exception 10 and 11
 *
 * These exceptions are for some Lloyds accounts and some TSB accounts. If there is a 10 in the
 * exception column for the first check for a sorting code and an 11 in the exception column for the
 * second check for the same sorting code, if either check is successful the account number is deemed
 * valid.
 * For the exception 10 check, if ab = 09 or ab = 99 and g = 9, zeroise weighting positions u-b.
 */
export const exception10_11: ModulusCheckOverrides = {
  ...baseExceptionHandling,
  replaceWeight: replaceForException10,
};

function replaceForException10(opts: ReplaceWeightOptions): Weight {
  const { account, weight } = opts;

  const a = account[positions['a']];
  const b = account[positions['b']];
  const g = account[positions['g']];

  if ((a === 0 && b === 9) || (a === 9 && b === 9 && g === 9)) {
    return zeroise_u_b(weight);
  }

  return weight;
}

/**
 * ## Exception 14
 *
 * Perform the modulus 11 check as normal:
 * - If the check passes (that is, there is no remainder), then the account number should be considered valid.
 * Do not perform the second check
 * - If the first check fails, then the second check must be performed as specified below.
 *
 * Second check:
 * - If the 8th digit of the account number (reading from left to right) is not 0, 1 or 9 then the account number
 * fails the second check and is not a valid Coutts account number
 * Specification by sorting code
 *
 * - If the 8th digit is 0, 1 or 9, then remove the digit from the account number and insert a 0 as the 1st digit
 * for check purposes only
 * - Perform the modulus 11 check on the modified account number using the same weightings as specified
 * in the table (that is, 0 0 0 0 0 0 8 7 6 5 4 3 2 1):
 *   - If there is no remainder, then the account number should be considered valid
 *   - If there is a remainder, then the account number fails the second check and is not a valid Coutts
 * account number
 */
export const exception14: ModulusCheckOverrides = {
  ...baseExceptionHandling,
  applyAccountNumberSubstitutions: substituteForException14,
};

function substituteForException14(
  opts: ApplyAccountNumberSubstitutionsOptions,
): string {
  const { accountNumber, checkNumber } = opts;
  if (checkNumber === 1) {
    if (
      accountNumber[7] === '0' ||
      accountNumber[7] === '1' ||
      accountNumber[7] === '9'
    ) {
      return '0' + accountNumber.slice(0, 7);
    }
  }

  return accountNumber;
}
